import { useMemo } from "react";
import {
  UserRole,
  AggregationField,
  ROLE_AGGREGATION_ACCESS,
} from "../types/roles";
import { MetricType } from "../types";

/**
 * Defines which metrics are accessible by each role
 * - Sales: Only sales-related metrics (revenue, quantity)
 * - Purchase: All metrics including cost/margin data
 */
const ROLE_METRIC_ACCESS: Record<UserRole, MetricType[]> = {
  Sales: [
    "revenue",
    "revenueDiffEuro",
    "revenueDiffPercent",
    "revenuePercentTotal",
    "quantity",
    "quantityDiffNumber",
    "quantityDiffPercent",
  ],
  Purchase: [
    "revenue",
    "revenueDiffEuro",
    "revenueDiffPercent",
    "revenuePercentTotal",
    "quantity",
    "quantityDiffNumber",
    "quantityDiffPercent",
    // Purchase-only metrics (cost/margin related)
    "cost",
    "costDiffEuro",
    "costDiffPercent",
    "profit",
    "profitDiffEuro",
    "profitDiffPercent",
    "margin",
    "marginDiffPercent",
  ],
};

/**
 * Metrics that require Purchase role to access
 */
export const PURCHASE_ONLY_METRICS: MetricType[] = [
  "cost",
  "costDiffEuro",
  "costDiffPercent",
  "profit",
  "profitDiffEuro",
  "profitDiffPercent",
  "margin",
  "marginDiffPercent",
];

const METRIC_AGGREGATION_DEPENDENCIES: Record<MetricType, AggregationField[]> =
  {
    revenue: ["revenue"],
    revenueDiffEuro: ["revenue"],
    revenueDiffPercent: ["revenue"],
    revenuePercentTotal: ["revenue"],
    quantity: ["quantity"],
    quantityDiffNumber: ["quantity"],
    quantityDiffPercent: ["quantity"],
    cost: ["cost"],
    costDiffEuro: ["cost"],
    costDiffPercent: ["cost"],
    profit: ["profit"],
    profitDiffEuro: ["profit"],
    profitDiffPercent: ["profit"],
    margin: ["revenue", "profit"],
    marginDiffPercent: ["revenue", "profit"],
  };

export function getRequiredAggregationFields(
  metrics: MetricType[],
): AggregationField[] {
  const required = new Set<AggregationField>();

  metrics.forEach((metric) => {
    METRIC_AGGREGATION_DEPENDENCIES[metric].forEach((field) => {
      required.add(field);
    });
  });

  return Array.from(required);
}

export function getPermittedAggregationFieldsForMetrics(
  role: UserRole,
  metrics: MetricType[],
): AggregationField[] {
  const requiredFields = getRequiredAggregationFields(metrics);
  const permittedFields = ROLE_AGGREGATION_ACCESS[role];
  const selectedFields = requiredFields.filter((field) =>
    permittedFields.includes(field),
  );

  // Keep grouped queries valid even if no metric is selected.
  if (selectedFields.length > 0) {
    return selectedFields;
  }

  return permittedFields.includes("revenue")
    ? ["revenue"]
    : [permittedFields[0]];
}

export interface UsePermissionsResult {
  /** Current user role */
  role: UserRole;
  /** Whether user has Purchase role (full access) */
  hasPurchaseAccess: boolean;
  /** Whether user has Sales role (limited access) */
  hasSalesAccess: boolean;
  /** List of metrics accessible by the current user */
  accessibleMetrics: MetricType[];
  /** Check if a specific metric is accessible */
  canAccessMetric: (metric: MetricType) => boolean;
  /** Filter an array of metrics to only include accessible ones */
  filterAccessibleMetrics: (metrics: MetricType[]) => MetricType[];
}

/**
 * Hook for checking user permissions based on their role
 */
export function usePermissions(userRole: UserRole): UsePermissionsResult {
  const hasPurchaseAccess = userRole === "Purchase";
  const hasSalesAccess = userRole === "Sales" || userRole === "Purchase";

  const accessibleMetrics = useMemo(() => {
    return ROLE_METRIC_ACCESS[userRole] || ROLE_METRIC_ACCESS.Sales;
  }, [userRole]);

  const canAccessMetric = useMemo(() => {
    return (metric: MetricType): boolean => {
      return accessibleMetrics.includes(metric);
    };
  }, [accessibleMetrics]);

  const filterAccessibleMetrics = useMemo(() => {
    return (metrics: MetricType[]): MetricType[] => {
      return metrics.filter((metric) => accessibleMetrics.includes(metric));
    };
  }, [accessibleMetrics]);

  return {
    role: userRole,
    hasPurchaseAccess,
    hasSalesAccess,
    accessibleMetrics,
    canAccessMetric,
    filterAccessibleMetrics,
  };
}
